/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  Button,
  CodeSnippet,
  CodeSnippetSkeleton,
  FormLabel,
  InlineNotification,
  ListItem,
  OrderedList,
  PasswordInput
} from 'carbon-components-react';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { sharedMessages } from '../../messages';
import { FIELDS } from '../schema';
import { PageStepMessages as messages } from './messages';

export const PageContent = ({
  isQuickSetup,
  readOnly
}: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, registerInputField, updateFieldValue } =
    useFormHelpers(formMethods);

  const accessTokenChangeState = useState<boolean>(false);
  const [regenerateVerifyTokenClicked, changeVerifyTokenClicked] =
    useState<boolean>(false);

  const regenerateVerifyToken = useCallback(() => {
    updateFieldValue(FIELDS.VERIFICATION_TOKEN, uuidv4());
    changeVerifyTokenClicked(true);
  }, [updateFieldValue]);

  return (
    <>
      <OrderedList className="TabContent__subtitle">
        <ListItem>
          <FormattedMessage
            values={{
              addProduct: <strong>{messages.addProduct.defaultMessage}</strong>,
              products: <strong>{messages.products.defaultMessage}</strong>
            }}
            {...messages.stepOne}
          />
        </ListItem>
        <ListItem>
          <FormattedMessage
            values={{
              addProductsToYourApp: (
                <strong>{messages.addProductsToYourApp.defaultMessage}</strong>
              ),
              messenger: <strong>{messages.messenger.defaultMessage}</strong>,
              setUp: <strong>{messages.setUp.defaultMessage}</strong>
            }}
            {...messages.stepTwo}
          />
        </ListItem>
        <ListItem>
          <FormattedMessage
            values={{
              generalTokens: (
                <strong>{messages.generalTokens.defaultMessage}</strong>
              ),
              connect: <strong>{messages.connect.defaultMessage}</strong>
            }}
            {...messages.stepThree}
          />
        </ListItem>
        <ListItem>
          <FormattedMessage
            values={{
              generateAccessToken: (
                <strong>{messages.generateAccessToken.defaultMessage}</strong>
              ),
              generate: <strong>{messages.generate.defaultMessage}</strong>
            }}
            {...messages.stepFour}
          />
        </ListItem>
      </OrderedList>
      <div className="TabContent__subtitle">
        <FormattedMessage
          values={{
            createANewPage: (
              <strong>{messages.createANewPage.defaultMessage}</strong>
            )
          }}
          {...messages.noteOne}
        />
        <div className="TabContent__spacer16" />
        <FormattedMessage {...messages.noteTwo} />
        <div className="TabContent__spacer16" />
        <PasswordInput
          {...registerInputField({
            field: FIELDS.PAGE_ACCESS_TOKEN,
            isPasswordField: true,
            isQuickSetup,
            labelText: messages.pageAccessTokenLabel,
            placeholderText: messages.pageAccessTokenPlaceholder,
            changeState: accessTokenChangeState,
            readOnly
          })}
          id="facebook-page-access-token"
        />
      </div>
      <div className="TabContent__spacer24" />
      <div className="TabContent__subtitle">
        <FormattedMessage
          values={{
            generatedVerifyTokenLabel: (
              <strong>
                {messages.generatedVerifyTokenLabel.defaultMessage}
              </strong>
            )
          }}
          {...messages.generatedVerifyTokenNote}
        />
      </div>
      <FormLabel className="TabContent__field-label">
        <FormattedMessage {...messages.generatedVerifyTokenLabel} />
      </FormLabel>
      {!_.isNil(getFieldValue(FIELDS.VERIFICATION_TOKEN)) ? (
        <>
          <CodeSnippet
            id="fb-generated-verify-token"
            aria-label={t(messages.generatedVerifyTokenLabel)}
            feedback={t(sharedMessages.copied)}
            copyButtonDescription={t(sharedMessages.copyToClipboard)}
            copyLabel={t(sharedMessages.copyToClipboard)}
            onClick={() =>
              copyContentToClipboard(getFieldValue(FIELDS.VERIFICATION_TOKEN))
            }
            type="single">
            {getFieldValue(FIELDS.VERIFICATION_TOKEN)}
          </CodeSnippet>
          {regenerateVerifyTokenClicked && (
            <InlineNotification
              lowContrast
              kind="warning"
              title={t(messages.notificationTitle)}
            />
          )}
        </>
      ) : regenerateVerifyTokenClicked ? (
        <CodeSnippetSkeleton />
      ) : (
        <Button
          id="fb-generated-verify-token"
          kind="primary"
          disabled={readOnly}
          onClick={regenerateVerifyToken}>
          {messages.regenerateVerifyToken.defaultMessage}
        </Button>
      )}
      <div className="TabContent__spacer24" />
    </>
  );
};
